import { useState, useRef, useEffect } from 'react';
import { AddAPhoto as AddAPhotoIcon, Collections as CollectionsIcon } from '@mui/icons-material';
import {
  Button,
  Input,
  ImageGalleryCardWrapper,
  ImageGalleryCard,
  Typography,
  ImageGallery,
  enqueueSnackbar,
  fullScreenButtonClick,
  FileRow,
  YardPlaceSelects,
} from '../../design-system';
import {
  DashboardFormPopup,
  DashboardHeader,
  CloseIconButton,
  HideInput,
  DashbaordFormFooter,
  DashboardInputs,
  DashboardAutoColumnInputs,
} from '../dashboard';
import { useStore } from '../../context';
import {
  useCallBackOnBack,
  useEditContainerMutation,
  useUpdateDocumentsMutation,
  useUpdateImagesMutation,
} from '../../hooks';
import { downloadBlob, filesFormatValidator, momentTextFormat, showToRoles } from '../../utils';
import { INPUT_ERRORS, MESSAGES } from '../../const';

interface ContainersViewProps {
  container: any;
  isOpen: boolean;
  refetch: () => void;
  handleFormCloseClick: () => void;
}

export const ContainersView: React.FC<ContainersViewProps> = ({
  isOpen,
  refetch,
  container,
  handleFormCloseClick,
}: ContainersViewProps) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const editContainerMutation = useEditContainerMutation();
  const updateImagesMutation = useUpdateImagesMutation();
  const updateDocumentsMutation = useUpdateDocumentsMutation();

  const [block, setBlock] = useState<string>('');
  const [row, setRow] = useState<string>('');
  const [slot, setSlot] = useState<string>('');
  const [tier, setTier] = useState<string>('');

  const [oldImages, setOldImages] = useState<any>([]);
  const [images, setImages] = useState<any>([]);
  const [imagesFiles, setImagesFiles] = useState<any>([]);
  const [isDeleteOldImage, setIsDeleteOldImage] = useState<boolean>(false);
  const [galleryImages, setGalleryImages] = useState<any[]>([]);
  const [zoomImageIndex, setZoomImageIndex] = useState<number>(1);
  const deleteOldPhotos = useRef<any>([]);
  const imagesInputRef = useRef<any>();
  const imagesGalleryInputRef = useRef<any>();

  const [oldFiles, setOldFiles] = useState<any>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [isDeleteOldFile, setIsDeleteOldFile] = useState<boolean>(false);
  const deleteOldFiles = useRef<any>([]);
  const filesInputRef = useRef<any>();

  useEffect(() => {
    if (!isOpen) {
      handleOnCancel();
      return;
    }

    setBlock(container.block || '');
    setRow(container.row != null && container.row !== '' ? String(container.row) : '');
    setSlot(container.slot != null && container.slot !== '' ? String(container.slot) : '');
    setTier(container.tier != null && container.tier !== '' ? String(container.tier) : '');
    setOldImages(container.images);
    setOldFiles(container.documents);
  }, [isOpen]);

  useCallBackOnBack(handleFormCloseClick, isOpen);

  const handleOnCancel = () => {
    deleteOldPhotos.current = [];

    setBlock(container.block || '');
    setRow(container.row != null && container.row !== '' ? String(container.row) : '');
    setSlot(container.slot != null && container.slot !== '' ? String(container.slot) : '');
    setTier(container.tier != null && container.tier !== '' ? String(container.tier) : '');
    setOldImages(container.images || []);
    setImages([]);
    setImagesFiles([]);
    setIsDeleteOldImage(false);

    setOldFiles(container.documents || []);
    setFiles([]);
    setIsDeleteOldFile(false);

    imagesInputRef.current.type = 'text';
    imagesInputRef.current.type = 'file';
    imagesGalleryInputRef.current.type = 'text';
    imagesGalleryInputRef.current.type = 'file';
    filesInputRef.current.type = 'text';
    filesInputRef.current.type = 'file';
  };

  const handleZoomImageClick = (i: number) => {
    setGalleryImages([...oldImages.map(({ url }: any) => url), ...images]);
    setZoomImageIndex(i);
    fullScreenButtonClick();
  };

  const handleChangeImages = (newImages: any, newFiles: any, isCapture?: boolean) => {
    if (filesFormatValidator(newFiles, 'Image')) {
      enqueueSnackbar(filesFormatValidator(newFiles, 'Image'), 'warning');
      return;
    }

    if (isCapture) {
      downloadBlob(newFiles[0], newFiles[0].name);
    }

    setImages([...images, ...newImages]);
    setImagesFiles([...imagesFiles, ...newFiles]);

    imagesInputRef.current.type = 'text';
    imagesInputRef.current.type = 'file';
    imagesGalleryInputRef.current.type = 'text';
    imagesGalleryInputRef.current.type = 'file';
  };

  const handleChangeFiles = (newImages: any, newFiles: any) => {
    if (filesFormatValidator(newFiles, 'Image')) {
      enqueueSnackbar(filesFormatValidator(newFiles, 'Image'), 'warning');
      return;
    }

    downloadBlob(newFiles[0], newFiles[0].name);

    setFiles([...files, ...newFiles]);

    filesInputRef.current.type = 'text';
    filesInputRef.current.type = 'file';
  };

  const handleDeleteImageClick = (i: number) => {
    if (i - oldImages.length >= 0) {
      i = i - oldImages.length;

      setImages(images.filter((_: any, index: number) => index !== i));
      setImagesFiles(imagesFiles.filter((_: any, index: number) => index !== i));
    } else {
      deleteOldPhotos.current = [
        ...deleteOldPhotos.current,
        { action: 'delete', name: oldImages[i].name },
      ];

      setOldImages(oldImages.filter((_: any, index: number) => index !== i));
      setIsDeleteOldImage(true);
    }
  };

  const handleDeleteFileClick = (i: number) => {
    if (i - oldFiles.length >= 0) {
      i = i - oldFiles.length;

      setFiles(files.filter((_: any, index: number) => index !== i));
    } else {
      deleteOldFiles.current = [
        ...deleteOldFiles.current,
        { action: 'delete', name: oldFiles[i].name },
      ];

      setOldFiles(oldFiles.filter((_: any, index: number) => index !== i));
      setIsDeleteOldFile(true);
    }
  };

  const handleSaveClick = async () => {
    if (images.length > 10) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }

    if (files.length > 10) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }

    const hasBlock = (!!block && !!row && !!slot) || (!block && !row && !slot);
    if (!hasBlock) {
      return;
    }

    const newPhotos = imagesFiles.map((file: any) => ({
      action: 'upload',
      file,
    }));

    const newDocuments = files.map((file) => ({
      action: 'upload',
      file,
    }));

    const containerEditMutate = () => {
      if (
        container.block !== block ||
        container.row !== row ||
        container.slot !== slot ||
        container.tier !== tier
      ) {
        return editContainerMutation.mutateAsync({
          id: container.id,
          block,
          row,
          slot,
          tier,
        });
      }

      return Promise.resolve();
    };

    const containerUploadImageMutate = () =>
      updateImagesMutation.mutateAsync({
        id: container.id,
        photos: [...newPhotos, ...deleteOldPhotos.current],
      });

    const containerUploadDocumentsMutate = () =>
      updateDocumentsMutation.mutateAsync({
        id: container.id,
        documents: [...newDocuments, ...deleteOldFiles.current],
      });

    await containerEditMutate();
    await containerUploadImageMutate();
    await containerUploadDocumentsMutate();

    refetch();
    handleFormCloseClick();
    enqueueSnackbar(MESSAGES.CONTAINERS.SUCCESS.FILES_CHANGE, 'success');
  };

  return (
    <DashboardFormPopup isOpen={isOpen}>
      <ImageGallery photos={galleryImages} startIndex={zoomImageIndex} />
      <DashboardHeader>
        <Typography variant={'textXL'} weight={'bold'}>
          {container.number}: {container.clientCode}
        </Typography>
        <CloseIconButton onClick={handleFormCloseClick} />
      </DashboardHeader>
      <DashboardInputs>
        <DashboardInputs>
          {isOpen && container.block && (
            <DashboardAutoColumnInputs>
              <YardPlaceSelects
                key={container.id}
                containerId={container.id}
                block={block}
                row={row}
                slot={slot}
                tier={tier}
                setBlock={setBlock}
                setRow={setRow}
                setSlot={setSlot}
                setTier={setTier}
                withDefaultValues
                isOptional={!block}
                isSubmitClicked={!!block}
                isOptionalTier
                disabled
                disabledRequest
              />
            </DashboardAutoColumnInputs>
          )}
          <Input
            label={'Gate-In Date'}
            data-size={'medium'}
            value={momentTextFormat(container.gateIn?.completedAt)}
            readOnly
          />
          <DashboardInputs columns={'1fr max-content max-content'}>
            <Typography variant={'textM'} weight={'bold'}>
              Images
            </Typography>
            <Button onClick={() => imagesGalleryInputRef.current.click()} isSquare>
              <AddAPhotoIcon />
            </Button>
            <Button onClick={() => imagesInputRef.current.click()} isSquare>
              <CollectionsIcon />
            </Button>
          </DashboardInputs>
          <HideInput>
            <Input
              id={'images'}
              label={'Container Images'}
              data-size={'medium'}
              type={'file'}
              inputRef={imagesGalleryInputRef}
              inputProps={{
                accept: '.jpeg, .jpg, .png, .webp',
                multiple: true,
                capture: 'environment',
              }}
              value={undefined}
              handleChangeFiles={(newImages, newFiles) =>
                handleChangeImages(newImages, newFiles, true)
              }
            />
            <Input
              id={'images'}
              label={'Container Images'}
              data-size={'medium'}
              type={'file'}
              inputRef={imagesInputRef}
              inputProps={{
                accept: '.jpeg, .jpg, .png, .webp',
                multiple: true,
              }}
              value={undefined}
              handleChangeFiles={handleChangeImages}
            />
          </HideInput>
          <ImageGalleryCardWrapper>
            {[...oldImages, ...images].map((img: any, i: any) => {
              let isHideDeleteButton = false;
              if (
                oldImages.length > i &&
                !showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN'], user)
              ) {
                isHideDeleteButton = true;
              }

              return (
                <ImageGalleryCard
                  key={i}
                  img={img.url || img}
                  handleZoomClick={() => handleZoomImageClick(i)}
                  handleDeleteImageClick={() => handleDeleteImageClick(i)}
                  hideDeleteButton={isHideDeleteButton || [...oldImages, ...images].length === 1}
                />
              );
            })}
          </ImageGalleryCardWrapper>
        </DashboardInputs>
        <DashboardInputs>
          <DashboardInputs columns={'1fr max-content'}>
            <Typography variant={'textM'} weight={'bold'}>
              Documents
            </Typography>
            <Button onClick={() => filesInputRef.current.click()} isSquare>
              <AddAPhotoIcon />
            </Button>
          </DashboardInputs>
          <HideInput>
            <Input
              id={'documents'}
              label={'Container Documents'}
              data-size={'medium'}
              type={'file'}
              inputRef={filesInputRef}
              inputProps={{
                accept: '.jpeg, .jpg, .png, .webp',
                multiple: true,
                capture: 'environment',
              }}
              value={undefined}
              handleChangeFiles={handleChangeFiles}
              withoutCompress
            />
          </HideInput>
          {!![...oldFiles, ...files].length &&
            [...oldFiles, ...files].map((file: any, i: any) => {
              let handleDeleteClick = showToRoles('admin', user)
                ? () => handleDeleteFileClick(i)
                : null;

              if (
                oldFiles.length > i &&
                !showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER'], user)
              ) {
                handleDeleteClick = null;
              }

              return (
                <FileRow
                  key={i}
                  file={file}
                  handleDeleteFileClick={handleDeleteClick}
                  variant="input"
                  withoutBorder
                />
              );
            })}
        </DashboardInputs>
      </DashboardInputs>
      <DashbaordFormFooter>
        <DashboardInputs columns={'1fr 1fr'}>
          <Button
            size={'medium'}
            data-color={'white'}
            onClick={handleOnCancel}
            disabled={
              !images.length &&
              !isDeleteOldImage &&
              !files.length &&
              !isDeleteOldFile &&
              block === container.block &&
              row === container.row &&
              slot === container.slot
            }
          >
            Cancel
          </Button>
          <Button
            size={'medium'}
            onClick={handleSaveClick}
            disabled={
              !images.length &&
              !isDeleteOldImage &&
              !files.length &&
              !isDeleteOldFile &&
              block === container.block &&
              row === container.row &&
              slot === container.slot &&
              tier === container.tier
            }
          >
            Save
          </Button>
        </DashboardInputs>
      </DashbaordFormFooter>
    </DashboardFormPopup>
  );
};
